import {useState, type FocusEvent, type FormEvent, type ReactNode} from 'react'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {validateRequired} from '@/lib/validation'

/** El techo del motivo, espejo de `MOTIVO_MAX_LENGTH` (core/bateria_cuarentena.py). */
const MOTIVO_MAX_LENGTH = 500

export function EliminarConMotivoDialog({
  open,
  onOpenChange,
  title,
  description,
  motivoLabel,
  motivoPlaceholder,
  confirmLabel,
  testIdPrefix,
  pending,
  error,
  onConfirm,
  onReset,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /** Como función recibe el nodo del `DialogContent`, que un `PorQue` necesita como `contenedor`. */
  description: ReactNode | ((contenedor: HTMLElement | null) => ReactNode)
  motivoLabel: string
  motivoPlaceholder: string
  confirmLabel: string
  testIdPrefix: string
  pending: boolean
  error: string | null
  onConfirm: (motivo: string) => void
  onReset: () => void
}) {
  const [contenedor, setContenedor] = useState<HTMLElement | null>(null)
  const [motivo, setMotivo] = useState('')
  const [motivoError, setMotivoError] = useState<string | undefined>()

  function close() {
    setMotivo('')
    setMotivoError(undefined)
    onReset()
    onOpenChange(false)
  }

  function blurMotivo(e: FocusEvent<HTMLTextAreaElement>) {
    setMotivoError(validateRequired(e.target.value) ?? undefined)
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    const trimmed = motivo.trim()
    const err = validateRequired(trimmed)
    setMotivoError(err ?? undefined)
    if (err) return
    onConfirm(trimmed)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent ref={setContenedor} className="max-w-lg" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="text-sm text-foreground" data-testid={`${testIdPrefix}-desc`}>
            {typeof description === 'function' ? description(contenedor) : description}
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${testIdPrefix}-motivo`}>{motivoLabel}</Label>
            <Textarea
              id={`${testIdPrefix}-motivo`}
              data-testid={`${testIdPrefix}-motivo`}
              value={motivo}
              onChange={(e) => {
                setMotivo(e.target.value)
                setMotivoError(undefined)
                onReset()
              }}
              onBlur={blurMotivo}
              maxLength={MOTIVO_MAX_LENGTH}
              minRows={3}
              aria-invalid={!!motivoError}
              placeholder={motivoPlaceholder}
            />
            <FieldError error={motivoError} />
          </div>
          <FormDialogFooter error={error} pending={pending} submitLabel={confirmLabel} onCancel={close} />
        </form>
      </DialogContent>
    </Dialog>
  )
}
